import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAppStore } from '../../store';

interface CategorySizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCategoryCreated: (category: string) => void;
  onSizeCreated: (size: string) => void;
}

const CategorySizeModal: React.FC<CategorySizeModalProps> = ({
  isOpen,
  onClose,
  onCategoryCreated,
  onSizeCreated,
}) => {
  const [newCategory, setNewCategory] = useState('');
  const [newSize, setNewSize] = useState('');
  const [activeTab, setActiveTab] = useState<'category' | 'size'>('category');

  const handleCreateCategory = () => {
    if (newCategory.trim()) {
      onCategoryCreated(newCategory.trim());
      setNewCategory('');
    }
  };

  const handleCreateSize = () => {
    if (newSize.trim()) {
      onSizeCreated(newSize.trim());
      setNewSize('');
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-lg">
          <Dialog.Title className="text-xl font-semibold mb-4">
            Crear Nueva Categoría o Talle
          </Dialog.Title>
          
          <div className="mb-4">
            <div className="flex border-b border-gray-200">
              <button
                className={`px-4 py-2 font-medium ${
                  activeTab === 'category'
                    ? 'border-b-2 border-indigo-500 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('category')}
              >
                Nueva Categoría
              </button>
              <button
                className={`px-4 py-2 font-medium ${
                  activeTab === 'size'
                    ? 'border-b-2 border-indigo-500 text-indigo-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('size')}
              >
                Nuevo Talle
              </button>
            </div>
          </div>

          {activeTab === 'category' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de la Categoría
                </label>
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Ej: Campera, Pantalón, etc."
                  onKeyPress={(e) => e.key === 'Enter' && handleCreateCategory()}
                />
              </div>
              <Button
                onClick={handleCreateCategory}
                disabled={!newCategory.trim()}
                icon={<Plus className="h-4 w-4" />}
              >
                Crear Categoría
              </Button>
            </div>
          )}

          {activeTab === 'size' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Talle
                </label>
                <Input
                  value={newSize}
                  onChange={(e) => setNewSize(e.target.value)}
                  placeholder="Ej: XS, XXL, 42, etc."
                  onKeyPress={(e) => e.key === 'Enter' && handleCreateSize()}
                />
              </div>
              <Button
                onClick={handleCreateSize}
                disabled={!newSize.trim()}
                icon={<Plus className="h-4 w-4" />}
              >
                Crear Talle
              </Button>
            </div>
          )}
          
          <div className="mt-6 flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cerrar
            </Button>
          </div>
          
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default CategorySizeModal;
